<script setup lang="ts">

import {
  GraphDataProviderColumn,
  GraphDataProviderQueryRoot,
  GraphDataProviderState,
  useContext,
  useEnums,
  useGraphDataProvider,
  useGraphMeta,
  usePhrases,
  useProcesses
} from "@hantera/portal-app";
import { ContextMenuSections, DataTableContextMenuProvider, ListDetailLayout, RadioButton, TextInput } from "@hantera/design-system";
import { computed, ref, toRaw, watch } from "vue";
import useBackend from "@/composition/useBackend";
import OrderDetails from "@/views/Sales/OrderDetails.vue";
import useGraphPhrases from "@/composition/use-graph-phrases";
import useGraphAvailableFields from "@/composition/use-graph-available-fields";

const context = useContext()

const { graph, user } = useBackend()
const graphMeta = useGraphMeta()
const processes = useProcesses()
const phrases = usePhrases()
const enums = useEnums()
const graphPhrases = useGraphPhrases()
const { getAvailableFields } = useGraphAvailableFields()

context.currentView!.value!.name.value = 'Orders'

const searchPhrase = ref(context.currentView.value?.query.phrase ?? '')

const stateFilter = ref('')

const query = computed<GraphDataProviderQueryRoot>(() => {

  let filter: string | undefined
  if (stateFilter.value) {
    filter = `orderState == '${stateFilter.value}'`
  }

  return {
    edge: 'orders',
    dataFields: ['orderState', 'orderId'],
    orderBy: 'createdAt desc',
    filter,
    phrase: searchPhrase.value,
    availableFields: getAvailableFields('order').map(c => ({
      field: c,
      label: graphPhrases.field('order', c)
    }))
  }
})

const defaultColumns = [{
  field: 'channelKey',
  queryPath: ['orders'],
}, {
  field: 'orderNumber',
  queryPath: ['orders'],
}, {
  field: 'createdAt',
  queryPath: ['orders'],
}, {
  field: 'orderTotal',
  queryPath: ['orders'],
}, {
  field: 'currencyCode',
  queryPath: ['orders'],
}, {
  field: 'tags',
  queryPath: ['orders'],
}]

const columns = ref<GraphDataProviderColumn[]>(
  user.value.settings['sales.orders.columns'] ?? Array.from(defaultColumns)
)

watch(columns, () => {
  user.updateSettings({
    'sales.orders.columns': toRaw(columns.value)
  })
}, {
  deep: true
})

const state = ref<GraphDataProviderState>(context.currentView.value!.state.listState ?? {
  filters: {},
  live: true
})

watch(state, () => {
  context.currentView.value!.state.listState = state.value
}, { deep: true })

const live = ref<boolean>(context.currentView.value!.state.live ?? false)

watch(live, () => {
  context.currentView.value!.state.live = live.value
})

const selection = ref<unknown>(context.currentView.value!.state.selection)

watch(selection, () => {
  context.currentView.value!.state.selection = selection.value
})

const provider = useGraphDataProvider(query, columns, state, {
  live: live,
  rowHue(row): string | number | undefined {

    switch (row['orders.orderState']) {
      case 'pending':
        return '--color-hue-yellow'
      case 'confirmed':
        return '--color-hue-green'
      case 'cancelled':
        return '--color-hue-red'
    }

    return
  }
})

const dataTableContextMenuProvider = computed<DataTableContextMenuProvider>(() => ({
  table: [[{
    label: 'Reset Layout',
    action: () => {
      columns.value = Array.from(defaultColumns)
      stateFilter.value = ''
    }
  }, {
    label: live.value ? 'Disable live updates' : 'Enable live updates',
    action() {
      live.value = !live.value
    }
  }, {
    label: 'Refresh',
    disabled: live.value,
    action() {
      provider.refresh()
    }
  }]]
}))

</script>

<template>
  <ListDetailLayout
    :provider="provider"
    :detail-component="OrderDetails"
    v-model:selection="selection"
    cache-key="sales_orders_layout"
    :dataTableContextMenuProvider="dataTableContextMenuProvider"
  >
    <template #list-toolbar>
      <div class="list-toolbar">
        <div class="state-selector">
          <RadioButton v-model="stateFilter" value="">All</RadioButton>
          <div class="separator"></div>
          <div class="btn-group">
            <RadioButton v-model="stateFilter" value="pending" class="pending">Pending</RadioButton>
            <RadioButton v-model="stateFilter" value="confirmed" class="confirmed">Confirmed</RadioButton>
            <RadioButton v-model="stateFilter" value="cancelled" class="cancelled">Cancelled</RadioButton>
          </div>
        </div>
        <div class="search">
          <TextInput type="search" v-model="searchPhrase" placeholder="Search" />
        </div>
      </div>
    </template>
  </ListDetailLayout>
</template>

<style scoped>
.list-toolbar {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 5px;
}

@container (max-width: 767px) {
  .list-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .search {
    width: 100%;
    order: 0;
  }

  .state-selector {
    order: 1;
  }
}

.state-selector {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 5px;
}

.separator {
  height: 100%;
  border-left: 1px solid hsl(var(--color-60));
}

.pending {
  --color-text: 0, 0%, 0%;
  --color-hue: var(--color-hue-yellow);
  --color-saturation: 100%;
  --color-lightness: 48.04%;
}

.confirmed {
  --color-text: 0, 100%, 100%;
  --color-hue: var(--color-hue-green);
  --color-saturation: 71.7%;
  --color-lightness: 41.57%;
}

.cancelled {
  --color-text: 0, 100%, 100%;
  --color-hue: var(--color-hue-red);
  --color-saturation: 71.7%;
  --color-lightness: 41.57%;
}
</style>
